import { Component, OnInit, ChangeDetectionStrategy, inject, DestroyRef, signal, effect } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { Observable, of, tap, catchError, switchMap, first, map } from 'rxjs';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MachineAccountsService } from '@shared/services/api/machine-accounts.service';
import { MachinesAccountIdsService } from '@shared/services/api/machines-account-ids.service';
import { Machine } from '@shared/entities/machine';
import { SalesOrder } from '@shared/entities/salesOrder';
import { SalesOrderFormService } from '@shared/services/api/sales-order-form.service';
import { Attachment } from '@shared/entities/attachment';
import { ThemeService } from '../services/theme.service';
import { FsAttachmentService } from '@shared/services/api/fs-attachment.service';
import { ProfileService } from '@shared/services/api/profile.service';
import { Roles } from '@shared/models/types';

interface MachineAccountOption {
  accountId: string;
  accountName: string;
}

@Component({
  selector: 'app-sales-order-form',
  templateUrl: './sales-order-form.component.html',
  styleUrl: './sales-order-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SalesOrderFormComponent implements OnInit {
  private _destroyRef = inject(DestroyRef);
  private _themeService = inject(ThemeService);
  private _formBuilder = inject(FormBuilder);
  private _machineAccountsService = inject(MachineAccountsService);
  private _machinesAccountIdsService = inject(MachinesAccountIdsService);
  private _salesOrderFormService = inject(SalesOrderFormService);
  private _profileService = inject(ProfileService);
  private route = inject(ActivatedRoute);
  private location = inject(Location);
  private router = inject(Router);
  private fsAttachmentService = inject(FsAttachmentService);

  isAdmin = toSignal(this._profileService.role$.pipe(map((role) => role === Roles.ADMIN)), { initialValue: false });

  isLight = signal(true);
  isSuccess = signal(false);
  isError = signal(false);
  errorMessage = signal('');
  deleteSuccess = signal(false);
  deleteError = signal(false);
  isLoading = signal(false);
  isDownloading = signal(false);
  isEdit = signal(false);

  form!: FormGroup;

  public _salesOrderId: string | null = null;
  public _salesOrder: SalesOrder = new SalesOrder();

  salesOrderFile: File | null = null;
  existingFile: Attachment | null = null;

  machineAccountOptions: MachineAccountOption[] = [];
  machinesByAccount: { accountId: string; accountName: string; machines: Machine[] }[] = [];
  selectedMachines: Machine[] = [];

  constructor() {
    effect(() => {
      if (this.form) {
        if (this.isEdit()) {
          this.form.disable();
        } else {
          this.form.enable();
        }
      }
    });
  }

  ngOnInit(): void {
    this.initializeForm();
    this.loadMachineAccounts();
    this.setupRouteSubscription();

    this._themeService.isLight$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((x) => this.isLight.set(x));
  }

  private setupRouteSubscription(): void {
    this.route.params.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((params) => {
      this._salesOrderId = params['id'] || null;
      this.isEdit.set(!!this._salesOrderId);
      if (this.isEdit() && this._salesOrderId) {
        this.loadSalesOrderData();
      }
    });
  }

  private initializeForm(): void {
    this.form = this._formBuilder.group({
      reqDocName: ['', Validators.required],
      machineAccount: [null, Validators.required],
      selectedMachines: [[]],
      salesOrderFile: [undefined]
    });

    this.form
      .get('machineAccount')
      ?.valueChanges.pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((accountId) => {
        if (accountId) {
          this.loadMachinesByAccountId(accountId);
        } else {
          this.clearMachinesData();
        }
      });
  }

  private loadSalesOrderData(): void {
    if (!this._salesOrderId) return;

    this.isLoading.set(true);
    this._salesOrderFormService
      .findByFilter(this._salesOrderId)
      .pipe(
        switchMap((salesOrder) =>
          salesOrder ? of(salesOrder) : this._salesOrderFormService.findSalesOrder(this._salesOrderId!)
        ),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: (salesOrder) => {
          if (salesOrder) {
            this._salesOrder = salesOrder;
            this.patchFormValues(salesOrder);
          }
          this.isLoading.set(false);
        },
        error: () => this.isLoading.set(false)
      });
  }

  private patchFormValues(salesOrder: SalesOrder): void {
    if (salesOrder.accountId && salesOrder.accountName) {
      const exists = this.machineAccountOptions.some((o) => o.accountId === salesOrder.accountId);
      if (!exists) {
        this.machineAccountOptions = [
          { accountId: salesOrder.accountId, accountName: salesOrder.accountName },
          ...this.machineAccountOptions
        ];
      }
    }

    this.form.patchValue({
      reqDocName: salesOrder.salesOrderNumber || '',
      machineAccount: salesOrder.accountId || ''
    });

    if (salesOrder.attachment) {
      this.existingFile = salesOrder.attachment;
    } else if (salesOrder.attachmentId) {
      this.existingFile = { id: salesOrder.attachmentId, fileName: 'Sales Order File' } as Attachment;
    }

    if (this.existingFile) {
      this.form.get('salesOrderFile')?.setValidators(null);
      this.form.get('salesOrderFile')?.updateValueAndValidity();
    }

    if (salesOrder.accountId) {
      this.loadMachinesByAccountId(salesOrder.accountId);
    }
  }

  private loadMachineAccounts(): void {
    this._machineAccountsService
      .getAllMachineAccounts()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (accounts) => {
          this.machineAccountOptions = accounts.map((account) => ({
            accountId: account.accountId || '',
            accountName: account.accountName || ''
          }));
        },
        error: (error) => console.error('Error loading machine accounts:', error)
      });
  }

  fetchMachineAccounts = (searchTerm: string): Observable<MachineAccountOption[]> => {
    if (!searchTerm || searchTerm.length < 2) {
      return of(this.machineAccountOptions.slice(0, 10));
    }

    return this._machineAccountsService.getMachineAccounts({ accountName: searchTerm, size: 10, page: 1 }).pipe(
      map((result) =>
        (result.data || []).map((account) => ({
          accountId: account.accountId || '',
          accountName: account.accountName || ''
        }))
      ),
      tap((options) => (this.machineAccountOptions = options)),
      catchError(() => of([]))
    );
  };

  private loadMachinesByAccountId(accountId: string): void {
    if (!accountId) {
      this.clearMachinesData();
      return;
    }

    this.isLoading.set(true);
    this._machinesAccountIdsService
      .getMachinesByAccountIds([accountId])
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (machines: Machine[]) => {
          const machinesMap = new Map<string, { accountId: string; accountName: string; machines: Machine[] }>();

          machines.forEach((machine: Machine) => {
            if (!machine.accountId) return;
            if (!machinesMap.has(machine.accountId)) {
              machinesMap.set(machine.accountId, {
                accountId: machine.accountId,
                accountName: machine.accountName || '',
                machines: []
              });
            }
            machinesMap.get(machine.accountId)?.machines.push(machine);
          });

          this.machinesByAccount = Array.from(machinesMap.values());

          if (!this.isEdit()) {
            this.selectedMachines = this.machinesByAccount.flatMap((group) => group.machines);
          } else if (this._salesOrder.machineInfos?.length) {
            const machineIds = this._salesOrder.machineInfos.map((m) => m.machineId);
            this.selectedMachines = this.machinesByAccount
              .flatMap((group) => group.machines)
              .filter((machine) => machineIds.includes(machine.id || ''));
          }

          this.form.patchValue({ selectedMachines: this.selectedMachines });
          this.isLoading.set(false);
        },
        error: (error) => {
          console.error('Error loading machines by account ID:', error);
          this.clearMachinesData();
          this.isLoading.set(false);
        }
      });
  }

  private clearMachinesData(): void {
    this.machinesByAccount = [];
    this.selectedMachines = [];
    this.form.patchValue({ selectedMachines: [] });
  }

  canEditMachines(): boolean {
    return !this.isEdit() || this.isAdmin();
  }

  onMachineSelection(machine: Machine, isChecked: boolean): void {
    if (!this.canEditMachines()) return;
    this.selectedMachines = isChecked
      ? [...this.selectedMachines, machine]
      : this.selectedMachines.filter((m) => m.id !== machine.id);
    this.form.patchValue({ selectedMachines: this.selectedMachines });
  }

  isMachineSelected(machineId: string): boolean {
    return this.selectedMachines.some((machine) => machine.id === machineId);
  }

  hasMachineSelectionChanged(): boolean {
    const originalIds = (this._salesOrder.machineInfos || [])
      .map((m) => m.machineId)
      .slice()
      .sort();
    const currentIds = this.selectedMachines
      .map((m) => m.id || '')
      .slice()
      .sort();
    return JSON.stringify(originalIds) !== JSON.stringify(currentIds);
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      this.salesOrderFile = input.files[0];
      this.form.get('salesOrderFile')?.setErrors(null);
    }
  }

  removeFileAttachment(): void {
    if (this.salesOrderFile) {
      this.salesOrderFile = null;
      this.form.get('salesOrderFile')?.setValue(undefined);
    } else {
      this.existingFile = null;
    }

    this.form.get('salesOrderFile')?.setValidators([Validators.required]);
    this.form.get('salesOrderFile')?.markAsDirty();
    this.form.get('salesOrderFile')?.updateValueAndValidity();
  }

  downloadAttachment(attachmentId?: string, fileName?: string): void {
    if (!attachmentId) return;
    this.isDownloading.set(true);
    this.fsAttachmentService
      .downloadFile(attachmentId, fileName || 'document')
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => this.isDownloading.set(false),
        error: () => this.isDownloading.set(false)
      });
  }

  isFileValid(): boolean {
    return !!(this.salesOrderFile || this.existingFile);
  }

  onSubmit(): void {
    if (this.form.invalid || !this.isFileValid()) {
      this.form.markAllAsTouched();
      if (!this.isFileValid()) this.form.get('salesOrderFile')?.markAsTouched();
      return;
    }

    this.isLoading.set(true);
    this.resetStateFlags();

    const salesOrder = this.buildSalesOrderFromForm();
    const files: Record<string, File> = {};
    if (this.salesOrderFile) {
      files['attachment'] = this.salesOrderFile;
    }

    const request =
      Object.keys(files).length > 0
        ? this.isEdit()
          ? this._salesOrderFormService.updateSalesOrderWithAttachments(this._salesOrderId!, salesOrder, files)
          : this._salesOrderFormService.saveSalesOrderWithAttachments(salesOrder, files)
        : this.isEdit()
          ? this._salesOrderFormService.editSalesOrder(salesOrder, this._salesOrderId!)
          : this._salesOrderFormService.saveSalesOrder(salesOrder);

    request.pipe(first(), takeUntilDestroyed(this._destroyRef)).subscribe({
      next: (res: any) => {
        this.isLoading.set(false);
        if (res?.error || res instanceof Error) {
          this.isError.set(true);
          this.errorMessage.set(res.error?.message || 'Error occurred');
        } else {
          this.isSuccess.set(true);
        }
      },
      error: (err) => {
        this.isLoading.set(false);
        this.isError.set(true);
        this.errorMessage.set(err?.error?.message || 'Error occurred');
      }
    });
  }

  private buildSalesOrderFromForm(): SalesOrder {
    const salesOrder = new SalesOrder();

    if (this.isEdit()) {
      // Only the matricole (machineInfos) are editable once an order exists;
      // the other fields are locked in the UI, so we resend the original values.
      salesOrder.id = this._salesOrderId!;
      salesOrder.salesOrderNumber = this._salesOrder.salesOrderNumber;
      salesOrder.accountId = this._salesOrder.accountId;
      salesOrder.accountName = this._salesOrder.accountName;
    } else {
      const selectedAccount = this.machineAccountOptions.find((a) => a.accountId === this.form.value.machineAccount);
      salesOrder.salesOrderNumber = this.form.value.reqDocName;
      salesOrder.accountId = this.form.value.machineAccount;
      salesOrder.accountName = selectedAccount?.accountName || '';
    }

    salesOrder.machineInfos = this.selectedMachines.map((m) => ({
      machineId: m.id || '',
      serialNumber: m.serialNumber || ''
    }));

    return salesOrder;
  }

  goToList() {
    this.router.navigate(['/repository']);
  }
  goBack() {
    this.location.back();
  }

  deleteSalesOrder(): void {
    if (!this._salesOrderId) return;
    this.isLoading.set(true);
    this.resetStateFlags();

    this._salesOrderFormService
      .deleteSalesOrder(this._salesOrderId)
      .pipe(first(), takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (res: any) => {
          this.isLoading.set(false);
          if (res === true) this.deleteSuccess.set(true);
          else {
            this.deleteError.set(true);
            this.errorMessage.set(this.extractErrorMessage(res));
          }
        },
        error: (err) => {
          this.isLoading.set(false);
          this.deleteError.set(true);
          this.errorMessage.set(this.extractErrorMessage(err?.error || err));
        }
      });
  }

  getDeleteButtonText() {
    return 'SALES_ORDER_FORM.DELETE_BUTTON';
  }
  getDeleteSuccessTitle() {
    return 'SALES_ORDER_FORM.DELETE_SUCCESS';
  }
  getDeleteSuccessDescription() {
    return 'SALES_ORDER_FORM.DELETED_SALES_ORDER_DESCRIPTION';
  }
  getDeleteErrorTitle() {
    return 'SALES_ORDER_FORM.DELETE_ERROR_TITLE';
  }
  getDeleteErrorDescription() {
    return 'SALES_ORDER_FORM.DELETE_ERROR_DESCRIPTION';
  }

  private extractErrorMessage(res: any): string {
    if (!res) return 'Unknown error occurred';
    if (res.message) return res.message;
    if (res.validation?.fields?.length)
      return `Validation errors:\n${res.validation.fields.map((f: any) => `${f.field}: ${f.error}`).join('\n')}`;
    return res.error || 'Unknown error occurred';
  }

  private resetStateFlags() {
    this.isSuccess.set(false);
    this.isError.set(false);
    this.deleteSuccess.set(false);
    this.deleteError.set(false);
    this.errorMessage.set('');
  }
}
